import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Trash2, CalendarClock, Users } from "lucide-react";
import { API, adminApi } from "./AdminDashboard";

const pad = (n) => String(n).padStart(2, "0");
const dateKey = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const CalendarTab = () => {
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [appointments, setAppointments] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => adminApi().get(`${API}/admin/appointments`).then((r) => setAppointments(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const byDate = useMemo(() => {
    const map = {};
    for (const a of appointments) (map[a.date] = map[a.date] || []).push(a);
    return map;
  }, [appointments]);

  const { y, m } = cursor;
  const startDay = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [...Array(startDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const monthName = new Date(y, m, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
  const todayKey = dateKey(now.getFullYear(), now.getMonth(), now.getDate());

  const shift = (dir) => setCursor((c) => {
    const nm = c.m + dir;
    return { y: c.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
  });

  const upcoming = appointments.filter((a) => a.date >= todayKey).slice(0, 10);
  const shown = selected ? (byDate[selected] || []) : upcoming;

  const remove = async (id) => {
    try {
      await adminApi().delete(`${API}/admin/appointments/${id}`);
      toast.success("Appointment removed.");
      load();
    } catch {
      toast.error("Could not remove appointment.");
    }
  };

  return (
    <div className="grid grid-cols-12 gap-4 lg:gap-8" data-testid="admin-calendar-tab">
      <div className="col-span-12 lg:col-span-7 rounded-2xl border border-[#E2E8E4] bg-[#F2F4F0] p-7">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl text-[#1A2F24]" data-testid="calendar-month-label">{monthName}</h3>
          <div className="flex gap-2">
            <button onClick={() => shift(-1)} data-testid="calendar-prev-btn" aria-label="Previous month"
              className="w-9 h-9 rounded-full border border-[#E2E8E4] flex items-center justify-center text-[#5C7368] hover:border-[#F4A261] hover:text-[#F4A261] transition-colors duration-300">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => shift(1)} data-testid="calendar-next-btn" aria-label="Next month"
              className="w-9 h-9 rounded-full border border-[#E2E8E4] flex items-center justify-center text-[#5C7368] hover:border-[#F4A261] hover:text-[#F4A261] transition-colors duration-300">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1.5 text-center text-[10px] tracking-[0.15em] uppercase text-[#5C7368] mb-2">
          {WEEKDAYS.map((d) => <span key={d}>{d}</span>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5" data-testid="calendar-grid">
          {cells.map((d, i) => {
            if (d === null) return <span key={`empty-${i}`} />;
            const k = dateKey(y, m, d);
            const count = byDate[k]?.length || 0;
            const isSelected = selected === k;
            return (
              <button key={k} onClick={() => setSelected(isSelected ? null : k)} data-testid={`calendar-day-${k}`}
                className={`relative aspect-square rounded-xl text-sm transition-all duration-300 flex flex-col items-center justify-center ${
                  isSelected ? "bg-[#1E352A] text-[#FAF9F6]"
                  : k === todayKey ? "bg-[#C4D7D1] text-[#1E352A] font-semibold"
                  : "bg-white/60 text-[#1A2F24] hover:border-[#F4A261] border border-[#E2E8E4]"
                }`}>
                {d}
                {count > 0 && (
                  <span className={`absolute bottom-1 w-4 h-4 rounded-full text-[9px] flex items-center justify-center ${isSelected ? "bg-[#F4A261] text-white" : "bg-[#F4A261] text-white"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="col-span-12 lg:col-span-5 rounded-2xl border border-[#E2E8E4] bg-white/60 p-7">
        <h4 className="font-display text-xl text-[#1A2F24]" data-testid="calendar-panel-title">
          {selected ? `Appointments on ${selected}` : "Upcoming Pantry Appointments"}
        </h4>
        <div className="mt-5 space-y-3" data-testid="calendar-appointments-list">
          {shown.length === 0 && <p className="text-sm text-[#5C7368]">No appointments to show.</p>}
          {shown.map((a) => (
            <div key={a.id} className="rounded-xl border border-[#E2E8E4] bg-[#FAF9F6] px-5 py-4 flex items-start justify-between gap-3" data-testid={`calendar-appointment-${a.id.slice(0, 8)}`}>
              <div>
                <p className="font-semibold text-sm text-[#1A2F24]">{a.name}</p>
                <p className="text-xs text-[#5C7368] mt-1 flex items-center gap-1.5">
                  <CalendarClock size={12} className="text-[#F4A261]" /> {a.date} · {a.time_slot}
                </p>
                <p className="text-xs text-[#5C7368] mt-1 flex items-center gap-1.5">
                  <Users size={12} className="text-[#F4A261]" /> {a.household_size} {a.household_size === 1 ? "person" : "people"}
                  {a.phone ? ` · ${a.phone}` : ""}
                </p>
                {a.notes && <p className="text-xs text-[#5C7368] mt-1 italic">{a.notes}</p>}
              </div>
              <button onClick={() => remove(a.id)} data-testid={`calendar-appointment-delete-${a.id.slice(0, 8)}`} aria-label="Delete appointment"
                className="w-8 h-8 rounded-full border border-[#E2E8E4] flex items-center justify-center text-[#5C7368] hover:border-red-400 hover:text-red-600 transition-colors duration-300 shrink-0">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarTab;
